/**
 * Total unread badge: room cache, debounced recompute, Matrix event subscriptions.
 */

import { captureChatException } from 'shared/sentry';
import { getRoomUnreadMessageCount, shouldRefreshRoomsForTimelineEvent } from '../../utils/matrix/matrixRoomUtils';
import { ROOM_UNREAD_CACHE_MAX_ENTRIES } from './matrixConstants';
import { isHealthySyncState, safeListener } from './matrixUtils';

/**
 * Drop left-over room ids and cap Map size (large accounts).
 * @param {import('./matrixUtils').MatrixService} service
 */
function pruneRoomUnreadCache(service) {
  if (!service.matrixClient) return;
  const rooms = service.matrixClient.getRooms?.() || [];
  const valid = new Set(rooms.map((r) => r?.roomId).filter(Boolean));
  for (const id of [...service.roomUnreadCache.keys()]) {
    if (!valid.has(id)) service.roomUnreadCache.delete(id);
  }
  if (service.roomUnreadCache.size > ROOM_UNREAD_CACHE_MAX_ENTRIES) {
    const overflow = service.roomUnreadCache.size - ROOM_UNREAD_CACHE_MAX_ENTRIES;
    if (overflow > 0) {
      let evicted = 0;
      for (const id of service.roomUnreadCache.keys()) {
        service.roomUnreadCache.delete(id);
        evicted += 1;
        if (evicted >= overflow) break;
      }
    }
  }
}

/**
 * Detach any unread listeners + timers.
 * @param {import('./matrixUtils').MatrixService} service
 */
export function teardownUnreadTotalTracking(service) {
  if (service.unreadTotalTimeout) {
    clearTimeout(service.unreadTotalTimeout);
    service.unreadTotalTimeout = null;
  }
  service.roomUnreadCache?.clear?.();
  service._unreadDirtyRoomIds?.clear?.();
  service._unreadFullRefreshPending = false;
  if (service.matrixClient && service.unreadTotalHandlers) {
    const h = service.unreadTotalHandlers;
    service.matrixClient.removeListener?.('Room.timeline', h.onTimeline);
    service.matrixClient.removeListener?.('Room', h.onRoom);
    service.matrixClient.removeListener?.('Event.decrypted', h.onEventDecrypted);
    service.matrixClient.removeListener?.('Room.receipt', h.onReceipt);
    service.matrixClient.removeListener?.('Room.accountData', h.onAccountData);
    service.matrixClient.removeListener?.('sync', h.onSync);
  }
  if (typeof document !== 'undefined' && service._onUnreadVisibilityChange) {
    document.removeEventListener('visibilitychange', service._onUnreadVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    if (service._onUnreadWindowFocus) {
      window.removeEventListener('focus', service._onUnreadWindowFocus);
    }
    if (service._onUnreadPageShow) {
      window.removeEventListener('pageshow', service._onUnreadPageShow);
    }
  }
  service._onUnreadVisibilityChange = null;
  service._onUnreadWindowFocus = null;
  service._onUnreadPageShow = null;
  service.unreadTotalHandlers = null;
}

/**
 * Attach Matrix listeners to maintain total unread count.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {string|null} myUserId
 */
export function setupUnreadTotalTracking(service, myUserId = null) {
  if (!service.matrixClient) {
    service.emitUnreadTotal(0);
    return;
  }

  teardownUnreadTotalTracking(service);

  const resolvedUserId = myUserId || service.matrixClient.getUserId?.() || null;
  if (!resolvedUserId) {
    service.emitUnreadTotal(0);
    return;
  }

  service.roomUnreadCache = new Map();
  service._unreadDirtyRoomIds = new Set();
  service._unreadFullRefreshPending = false;

  const recomputeRoomUnread = (room) => {
    if (!room?.roomId) return;
    try {
      const unread = getRoomUnreadMessageCount(room, resolvedUserId);
      service.roomUnreadCache.set(room.roomId, Math.max(unread, 0));
    } catch (e) {
      captureChatException(e, { tags: { matrix: 'unread', action: 'room' } });
    }
  };

  const recomputeAllRoomUnreads = () => {
    service.roomUnreadCache.clear();
    const rooms = service.matrixClient.getRooms?.() || [];
    for (const room of rooms) {
      if (room) recomputeRoomUnread(room);
    }
    pruneRoomUnreadCache(service);
  };

  const totalFromUnreadCache = () => {
    let total = 0;
    for (const n of service.roomUnreadCache.values()) total += n;
    return total;
  };

  let hasInitialEmit = false;
  const schedule = ({ full = false, roomId = null } = {}) => {
    if (full) service._unreadFullRefreshPending = true;
    if (roomId) service._unreadDirtyRoomIds.add(roomId);
    if (!hasInitialEmit) {
      const syncState = service.matrixClient.getSyncState?.() || null;
      if (!isHealthySyncState(syncState)) return;
      const rooms = service.matrixClient.getRooms?.() || [];
      if (rooms.length === 0) return;
      hasInitialEmit = true;
      service._unreadFullRefreshPending = true;
    }
    if (service.unreadTotalTimeout) clearTimeout(service.unreadTotalTimeout);
    service.unreadTotalTimeout = setTimeout(() => {
      try {
        const runFull = service._unreadFullRefreshPending;
        if (runFull) {
          service._unreadFullRefreshPending = false;
          service._unreadDirtyRoomIds.clear();
          recomputeAllRoomUnreads();
        } else {
          for (const id of service._unreadDirtyRoomIds) {
            const room = service.matrixClient.getRoom?.(id);
            if (room) recomputeRoomUnread(room);
            else service.roomUnreadCache.delete(id);
          }
          service._unreadDirtyRoomIds.clear();
          pruneRoomUnreadCache(service);
        }
        service.emitUnreadTotal(totalFromUnreadCache());
      } catch (e) {
        captureChatException(e, { tags: { matrix: 'unread', action: 'total' } });
        service.emitUnreadTotal(0);
      }
      service.unreadTotalTimeout = null;
    }, 250);
  };

  const onTimeline = safeListener((event, room, toStartOfTimeline) => {
    if (toStartOfTimeline) {
      schedule({ full: true });
      return;
    }
    if (!event) return;
    if (shouldRefreshRoomsForTimelineEvent(event) || event.isEncrypted?.() === true) {
      schedule({ roomId: room?.roomId || event.getRoomId?.() || null });
    }
  }, 'Room.timeline:unreadTotal');

  const onRoom = safeListener(() => schedule({ full: true }), 'Room:unreadTotal');

  const onEventDecrypted = safeListener((event, err) => {
    if (err || !event) return;
    if (shouldRefreshRoomsForTimelineEvent(event)) {
      const rid = event.getRoomId?.() || null;
      schedule({ roomId: rid });
    }
  }, 'Event.decrypted:unreadTotal');

  const onReceipt = safeListener((event, room) => {
    schedule({ roomId: room?.roomId || event?.getRoomId?.() || null });
  }, 'Room.receipt:unreadTotal');
  const onAccountData = safeListener((event) => {
    const type = event?.getType?.() || event?.type;
    if (type === 'm.fully_read') {
      const rid = event.getStateKey?.() || event.stateKey || null;
      schedule({ roomId: rid, full: !rid });
    }
  }, 'Room.accountData:unreadTotal');

  const onSync = safeListener((state, prevState) => {
    if (state === 'PREPARED' || (state === 'SYNCING' && prevState !== 'SYNCING')) {
      schedule({ full: true });
    }
  }, 'sync:unreadTotal');

  service.unreadTotalHandlers = { onTimeline, onRoom, onEventDecrypted, onReceipt, onAccountData, onSync };

  schedule();

  // Ensure footer badge catches up quickly when returning after background/idle.
  service._onUnreadVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    schedule({ full: true });
  };
  service._onUnreadWindowFocus = () => {
    schedule({ full: true });
  };
  service._onUnreadPageShow = () => {
    schedule({ full: true });
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', service._onUnreadVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', service._onUnreadWindowFocus);
    window.addEventListener('pageshow', service._onUnreadPageShow);
  }

  service.matrixClient.on('Room.timeline', onTimeline);
  service.matrixClient.on('Room', onRoom);
  service.matrixClient.on('Event.decrypted', onEventDecrypted);
  service.matrixClient.on('Room.receipt', onReceipt);
  service.matrixClient.on('Room.accountData', onAccountData);
  service.matrixClient.on('sync', onSync);
}
